/**
 * Logger Service
 * Centralized logging with level control and formatting
 */

import { environment } from '@config/environment.config';

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  NONE = 4,
}

const LOG_LEVEL_MAP: Record<string, LogLevel> = {
  debug: LogLevel.DEBUG,
  info: LogLevel.INFO,
  warn: LogLevel.WARN,
  error: LogLevel.ERROR,
  none: LogLevel.NONE,
};

interface LogEntry {
  level: LogLevel;
  timestamp: string;
  message: string;
  data?: unknown;
  context?: string;
}

class Logger {
  private currentLevel: LogLevel;
  private logs: LogEntry[] = [];
  private maxLogs: number = 1000;

  constructor() {
    const configLevel = environment.get().logLevel;
    this.currentLevel = LOG_LEVEL_MAP[configLevel] ?? LogLevel.INFO;
  }

  private shouldLog(level: LogLevel): boolean {
    return environment.get().enableLogging && level >= this.currentLevel;
  }

  private formatMessage(level: LogLevel, message: string, context?: string): string {
    const levelStr = LogLevel[level];
    const timestamp = new Date().toISOString();
    const contextStr = context ? `[${context}]` : '';
    return `[${timestamp}] [${levelStr}]${contextStr} ${message}`;
  }

  private log(level: LogLevel, message: string, data?: unknown, context?: string): void {
    if (!this.shouldLog(level)) {
      return;
    }

    const formattedMessage = this.formatMessage(level, message, context);
    const logEntry: LogEntry = {
      level,
      timestamp: new Date().toISOString(),
      message,
      data,
      context,
    };
    // Store log entry
    this.logs.push(logEntry);
    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
    }

    // Console output
    switch (level) {
      case LogLevel.DEBUG:
        console.log(formattedMessage, data ?? '');
        break;
      case LogLevel.INFO:
        console.info(formattedMessage, data ?? '');
        break;
      case LogLevel.WARN:
        console.warn(formattedMessage, data ?? '');
        break;
      case LogLevel.ERROR:
        console.trace(message)
        console.error(formattedMessage, data ?? '');
        break;
    }
  }

  public debug(message: string, data?: unknown, context?: string): void {
    this.log(LogLevel.DEBUG, message, data, context);
  }

  public info(message: string, data?: unknown, context?: string): void {
    this.log(LogLevel.INFO, message, data, context);
  }

  public warn(message: string, data?: unknown, context?: string): void {
    this.log(LogLevel.WARN, message, data, context);
  }

  public error(message: string, error?: Error | unknown, context?: string): void {
    const errorData = error instanceof Error
      ? {
          name: error.name,
          message: error.message,
          stack: error.stack,
        }
      : error;
    
    this.log(LogLevel.ERROR, message, errorData, context);
  }

  public setLevel(level: LogLevel): void {
    this.currentLevel = level;
  }

  public getLogs(): readonly LogEntry[] {
    return [...this.logs];
  }

  public clearLogs(): void {
    this.logs = [];
  }

  public exportLogs(): string {
    return JSON.stringify(this.logs, null, 2);
  }

  public downloadLogs(): void {
    const logsJson = this.exportLogs();
    const blob = new Blob([logsJson], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `document-editor-logs-${Date.now()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
}

// Singleton instance
export const logger = new Logger();

// Context-specific loggers
export function createContextLogger(context: string) {
  return {
    debug: (message: string, data?: unknown) => logger.debug(message, data, context),
    info: (message: string, data?: unknown) => logger.info(message, data, context),
    warn: (message: string, data?: unknown) => logger.warn(message, data, context),
    error: (message: string, error?: Error | unknown) => logger.error(message, error, context),
  };
}

// Named exports for convenience
export const log = {
  debug: logger.debug.bind(logger),
  info: logger.info.bind(logger),
  warn: logger.warn.bind(logger),
  error: logger.error.bind(logger),
};